import React, { useEffect, useState } from 'react';
import { Skeleton, Modal } from 'antd';
import { HelpInfo, HelpInfosFilter } from 'types';
import { useDispatch, useSelector } from 'react-redux';
import history from 'app-router/history';
import { useLocation } from 'react-router';

import HelpInfoList from 'components/templates/help-info-list';
import ButtonPrimary from 'components/molecules/button-primary';
import Layout from 'components/templates/layout';
import { useIntl } from 'common/config/i18n';
import ModalContentConfirm from 'components/organisms/modal-content-confirm';
import {
  helpInfosActions,
  helpInfosSelectors,
  helpInfoActions,
  helpInfoSelectors,
  SituationsActions,
  UserInfoSelectors,
  ClearStateSliceActions,
  SituationsSelectors,
  ServiceTypesSelectors,
} from 'common/config/store';
import { StyledMainTitle, StyledListUtil } from 'common/styling/mixins';
import { isRequestError } from 'common/utils/error';
import FormFilter from 'components/templates/form-filter';
import MessageModal from 'components/organisms/message-modal';
import { htmlParser } from 'common/utils/html-parser';

export interface State {
  id?: string;
}

const { StyledListWrapper, StyledListTitleWrapper, StyledListButtonWrapper, StyledListBoldWrapper } = StyledListUtil;

const HelpInfos: React.FC<State> = () => {
  const dispatch = useDispatch();
  const { t } = useIntl();
  const { state: locationFilterValues } = useLocation<HelpInfosFilter>();

  const [modalVisible, setModalVisible] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [helpInfo, setHelpInfo] = useState({ id: 0, Name: '' });
  const [filterValues, setFilterValues] = useState<HelpInfosFilter>();

  const status = useSelector(helpInfosSelectors.statusSelector);
  const data = useSelector(helpInfosSelectors.dataSelector);
  const canWriteHelpInfos = useSelector(UserInfoSelectors.hasWriteAccess('Help-Info'));
  const error = useSelector(helpInfoSelectors.errorSelector);
  const errorType = useSelector(helpInfoSelectors.errorTypeSelector);
  const situationsStatus = useSelector(SituationsSelectors.statusSelector);
  const situations = useSelector(SituationsSelectors.situationsSelector);
  const serviceTypesStatus = useSelector(ServiceTypesSelectors.statusSelector);
  const serviceTypes = useSelector(ServiceTypesSelectors.serviceTypesSelector);

  useEffect(() => {
    if (locationFilterValues) {
      dispatch(
        helpInfosActions.fetchHelpInfos({
          ...locationFilterValues,
          pageNumber: 1,
          maxResultPage: 10,
        })
      );
      setFilterValues(locationFilterValues);
      /* 
        on page reload fixed to prevent using the location.state
        -> clear the state without triggering a rerender
      */
      window.history.replaceState({}, document.title);
    } else {
      dispatch(helpInfosActions.fetchHelpInfos({ pageNumber: 1, maxResultPage: 10 }));
    }
    dispatch(SituationsActions.fetchSituations());
  }, [dispatch, locationFilterValues]);

  useEffect(() => {
    if (errorType && errorType === 'delete') setShowDeleteModal(true);
  }, [errorType]);

  const onClickCreateHandler = (): void => {
    return history.push('/help-info');
  };

  const onClickEditHandler = (helpInfo: HelpInfo): void => {
    return history.push(`/help-info/${helpInfo.id}`, filterValues);
  };

  const onClickDeleteHandler = (helpInfo: HelpInfo): void => {
    setHelpInfo({ id: helpInfo.id, Name: `${helpInfo?.longMessage?.substring(0, 100)}...` });
    setModalVisible(true);
  };

  const onModalConfirm = (shouldRemove: boolean) => {
    if (shouldRemove) {
      dispatch(
        helpInfoActions.deleteHelpInfo(helpInfo.id.toString(), {
          ...filterValues,
          pageNumber: 1,
          maxResultPage: 10,
        })
      );
    }
    setModalVisible(false);
  };

  const fetchListAction = (values?: Omit<HelpInfosFilter, 'pageNumber' | 'maxResultPage'>) => {
    setFilterValues(values);
    dispatch(
      helpInfosActions.fetchHelpInfos({
        ...values,
        pageNumber: 1,
        maxResultPage: 10,
      })
    );
  };

  return (
    <div>
      <Modal
        title={t('Modal', 'HELPINFO_MODAL_TITLE')}
        footer={null}
        open={modalVisible}
        onCancel={() => onModalConfirm(false)}
      >
        <ModalContentConfirm
          title={
            <div>
              {t('Modal', 'HELPINFO_DELETE_CONFIRM')}
              <StyledListBoldWrapper className="ql-editor">{htmlParser(helpInfo.Name)}</StyledListBoldWrapper>
            </div>
          }
          onConfirm={onModalConfirm}
        />
      </Modal>
      <MessageModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          dispatch(ClearStateSliceActions.clearStateSlice({ sliceReducer: 'HelpInfo' }));
        }}
        errorMessage={error && isRequestError(error) ? undefined : error}
      />
      <Layout />
      <StyledListWrapper>
        <StyledListTitleWrapper>
          <StyledMainTitle>{t('menu', 'MENU_ITEM_3_1_1_1_1')}</StyledMainTitle>
          <StyledListButtonWrapper>
            {canWriteHelpInfos && (
              <ButtonPrimary onClick={onClickCreateHandler}>{t('Buttons', 'ADD_HELP_BUTTON')}</ButtonPrimary>
            )}
          </StyledListButtonWrapper>
        </StyledListTitleWrapper>
        <FormFilter
          fetchListAction={fetchListAction}
          filterValues={filterValues}
          situationsLoading={situationsStatus === 'loading'}
          situations={situations}
          serviceTypesLoading={serviceTypesStatus === 'loading'}
          serviceTypes={serviceTypes}
        />
        {status === 'loading' && <Skeleton />}
        {status === 'success' && (
          <HelpInfoList
            isAdmin={canWriteHelpInfos}
            data={data}
            onClickEdit={onClickEditHandler}
            onClickDelete={onClickDeleteHandler}
            filterValues={filterValues}
            serviceTypes={serviceTypes}
          />
        )}
      </StyledListWrapper>
    </div>
  );
};

export default HelpInfos;
